import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from './auth/AuthProvider';
import { getUserProfile } from '../utils/profileUtils';
import {
  FaPlay,
  FaBookOpen
} from 'react-icons/fa';
import '../assets/styles/HomePageBase.css';
import '../assets/styles/LandingLayout.css';

const HomePageBase = ({ variant = 'auth', sessionData }) => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile for conditional rendering
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileOptions = user?.sub ? { userId: user.sub } : {};
        const profile = await getUserProfile(profileOptions);
        
        if (profile) {
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    if (variant === 'auth' && user) {
      fetchUserProfile();
    }
  }, [user, variant]);

  const firstName = 
    userProfile?.firstName || 
    userProfile?.name?.split(' ')[0] || 
    user?.firstName ||
    user?.name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    '';

  // Optimized animation variants - disable on mobile
  const containerVariants = isMobile ? {} : {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05
      }
    }
  };

  const MathVisualization = () => {

    return (
      <div className="unified-showcase">
        <div className="showcase-container landing-section">
          {/* Header - Title and Subtitle */}
          <div className="showcase-header">
            <h1 className="showcase-title">
              AI Maths Tutor
            </h1>
            
            <h2 className="showcase-subtitle">
              Understand Mathematics with Confidence
            </h2>

            {/* Feature bullet points for public variant */}
            {variant === 'public' && (
              <div className="feature-bullets">
                <ul>
                  <li>Tutoring lessons personalised to your textbook</li>
                  <li>24/7 access to homework help</li>
                  <li>Personalised learning and difficulty levels</li>
                  <li>Comprehensive exam prep (coming soon)</li>
                </ul>
              </div>
            )}

            {variant === 'auth' && firstName && (
              <div className="showcase-welcome authenticated">
                <div className="showcase-welcome-message">
                  Welcome back, <strong>{firstName}</strong>! Ready to continue learning?
                </div>
              </div>
            )}
          </div>

          {/* Modern CTAs - Only show for public variant */}
          {variant === 'public' && (
            <div className="modern-cta-section">
              <div className="primary-ctas">
                <Link to="/early-access" className="cta-button primary">
                  Request Free Early Access
                </Link>
                <button 
                  className="cta-button secondary"
                  onClick={() => {
                    document.querySelector('.image-preview-page')?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }}
                >
                  Explore AI Math Tutor
                </button>
              </div>
              <div className="auth-links">
                <span>Already have access? </span>
                <Link to="/login" className="auth-link">Log In</Link>
              </div>
            </div>
          )}

          {/* Feature Cards - Only show for authenticated users */}
          {variant === 'auth' && (
            <div className="feature-cards">
              <div className="feature-card">
                <div className="card-content">
                  <div className="card-number">1.</div>
                  <h3 className="card-title">On-Demand Tutoring Lessons</h3>
                  <p className="card-description">
                    Get personalised AI tutoring on any math topic. Choose your lesson and start learning 
                    immediately with interactive, curriculum-aligned content.
                  </p>
                </div>
                <Link 
                  to={variant === 'auth' ? "/tutor" : "/early-access"} 
                  className="card-button tertiary"
                >
                  <FaPlay />
                  <span>Start Tutoring</span>
                </Link>
              </div>

              <div className="feature-card">
                <div className="card-content">
                  <div className="card-number">2.</div>
                  <h3 className="card-title">Get Instant Support</h3>
                  <p className="card-description">
                    AI-powered assistance breaks down complex problems into manageable steps. 
                    Upload your homework and get step-by-step solutions.
                  </p>
                </div>
                <Link 
                  to={variant === 'auth' ? "/homework-help" : "/early-access"} 
                  className="card-button tertiary"
                >
                  <FaBookOpen />
                  <span>Homework Help</span>
                </Link>
              </div>

              <div className="feature-card">
                <div className="card-content">
                  <div className="card-number">3.</div>
                  <h3 className="card-title">Ace Your Exams</h3>
                  <p className="card-description">
                    Build unshakeable confidence with comprehensive exam preparation and practice tests. 
                    Get ready for any mathematics assessment.
                  </p>
                </div>
                <Link 
                  to="/early-access" 
                  className="card-button tertiary"
                >
                  <span>📚</span>
                  <span>Exam Prep</span>
                  <span className="coming-soon">Coming Soon</span>
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  };

  const MotionWrapper = isMobile ? 'div' : motion.div;

  return (
    <MotionWrapper 
      className="home-container-unified"
      {...(!isMobile && {
        variants: containerVariants,
        initial: "hidden",
        animate: "visible"
      })}
    >
      <div className="main-showcase-section">
        <MathVisualization />
      </div>
    </MotionWrapper>
  );
};

export default HomePageBase;